/**
 * SkillLeaderboardPanel — GM Console tab.
 *
 * Shows the top N (configurable, default 3) players per gathering/crafting/
 * enchanting skill so GMs can spot balance outliers.
 * Consumes GET /api/admin/gm/skill-leaderboard via api.gmSkillLeaderboard().
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, RefreshCw, Loader2 } from "lucide-react";

const SKILL_LABELS = {
  woodcutting: "Taglialegna",
  mining:      "Minatore",
  fishing:     "Pescatore",
  gathering:   "Raccoglitore",
  crafting:    "Artigiano",
  enchanting:  "Incantatore",
};

const SKILL_ORDER = ["woodcutting", "mining", "fishing", "gathering", "crafting", "enchanting"];

export default function SkillLeaderboardPanel() {
  const [topN, setTopN] = useState(3);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async (n = topN) => {
    setLoading(true);
    try {
      const r = await api.gmSkillLeaderboard(n);
      setData(r?.leaderboard || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento leaderboard fallito");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(topN); /* eslint-disable-next-line */ }, []);

  const skills = useMemo(
    () => SKILL_ORDER.filter(k => Array.isArray(data[k])),
    [data]
  );

  return (
    <div className="space-y-6" data-testid="skill-leaderboard-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-semibold">Classifica Skill</h2>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm text-neutral-300 flex items-center gap-2">
            Top
            <select
              data-testid="skill-leaderboard-topn-select"
              value={topN}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10) || 3;
                setTopN(n);
                load(n);
              }}
              className="bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-sm"
            >
              {[1, 3, 5, 10].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>

          <button
            data-testid="skill-leaderboard-refresh-btn"
            onClick={() => load(topN)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-sm disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            Aggiorna
          </button>
        </div>
      </div>

      {loading && skills.length === 0 ? (
        <div className="text-neutral-400 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
        </div>
      ) : skills.length === 0 ? (
        <div className="text-neutral-400 text-sm">
          Nessun dato disponibile. Nessun giocatore ha ancora allenato una skill.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {skills.map((skill) => {
            const rows = data[skill] || [];
            return (
              <div
                key={skill}
                data-testid={`skill-leaderboard-table-${skill}`}
                className="rounded-lg border border-neutral-800 bg-neutral-900/60 p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-neutral-100">
                    {SKILL_LABELS[skill] || skill}
                  </h3>
                  <span className="text-xs text-neutral-500 uppercase tracking-wide">
                    {skill}
                  </span>
                </div>

                {rows.length === 0 ? (
                  <div className="text-xs text-neutral-500 py-2">
                    Nessun giocatore in classifica.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-neutral-400 text-xs uppercase">
                        <th className="text-left py-1">#</th>
                        <th className="text-left py-1">Nome</th>
                        <th className="text-right py-1">Lv</th>
                        <th className="text-right py-1">XP</th>
                        <th className="text-right py-1">Char Lv</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr
                          key={r.user_id || i}
                          className="border-t border-neutral-800/70"
                        >
                          <td className="py-1 text-neutral-500">{i + 1}</td>
                          <td className="py-1 text-neutral-100">{r.name}</td>
                          <td className="py-1 text-right text-amber-300 font-semibold">
                            {r.level}
                          </td>
                          <td className="py-1 text-right text-neutral-300">
                            {r.xp?.toLocaleString?.() ?? r.xp}
                          </td>
                          <td className="py-1 text-right text-neutral-400">
                            {r.character_level}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
